import type { PostHog, PostHogConfig } from "posthog-js"

type PostHogTask = (client: PostHog) => void
type CaptureProperties = Parameters<PostHog["capture"]>[1]
type CaptureOptions = Parameters<PostHog["capture"]>[2]

export const MAX_QUEUED_ANALYTICS_TASKS = 100

const loadPostHog = () => import("posthog-js").then(mod => mod.default)

let loadedClient: PostHog | null = null
let isStarting = false
const queuedTasks: PostHogTask[] = []

export function withPostHog(task: PostHogTask): void {
    if (loadedClient) {
        task(loadedClient)
        return
    }
    if (queuedTasks.length < MAX_QUEUED_ANALYTICS_TASKS) queuedTasks.push(task)
}

export function changePostHogIdentity(task: PostHogTask): void {
    if (loadedClient) {
        task(loadedClient)
        return
    }
    queuedTasks.push(task)
}

export function captureAnalyticsEvent(
    event: string,
    properties?: CaptureProperties,
    options?: CaptureOptions
): void {
    withPostHog(client => client.capture(event, properties, options))
}

export async function startPostHog({
    apiKey,
    config,
    load = loadPostHog,
}: {
    apiKey: string
    config: Partial<PostHogConfig>
    load?: () => Promise<PostHog>
}): Promise<void> {
    if (isStarting || loadedClient) return
    isStarting = true
    try {
        const client = await load()
        client.init(apiKey, config)
        loadedClient = client
        queuedTasks.splice(0).forEach(task => task(client))
    } catch {
        // A network that blocks the PostHog chunk loses analytics only; the app is unaffected
        isStarting = false
    }
}

export function resetAnalyticsForTests(): void {
    loadedClient = null
    isStarting = false
    queuedTasks.length = 0
}
